import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  MessageSquare,
  BarChart3,
  Menu,
  X,
  Bus,
  Users,
  Wrench,
  Hammer,
  DollarSign,
  LogOut,
  ShieldAlert,
  FileSpreadsheet,
  Archive,
  Receipt,
  Sparkles,
  CreditCard,
  CalendarCheck,
  Cog,
  HelpCircle,
  Lock,
  FileText
} from 'lucide-react';
import { useState } from 'react';

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
}

const navigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Ventas', href: '/ventas', icon: ShoppingCart },
  { name: 'Stock', href: '/stock', icon: Package },
  { name: 'Herramientas', href: '/stock?tipo=herramienta', icon: Hammer },
  { name: 'Buses', href: '/buses', icon: Bus },
  { name: 'Créditos', href: '/creditos', icon: CreditCard },
  { name: 'Mecánicos', href: '/mecanicos', icon: Users },
  { name: 'Asistencia', href: '/asistencia', icon: CalendarCheck },
  { name: 'Mantenimientos', href: '/mantenimientos', icon: Wrench },
  { name: 'Pago de Mantenimiento', href: '/pago-mantenimiento', icon: Wrench },
  { name: 'Quejas', href: '/quejas', icon: MessageSquare },
  { name: 'Caja Mantenimiento', href: '/caja-mantenimiento', icon: Wrench },
  { name: 'Caja Servicios', href: '/caja-servicios', icon: Sparkles },
  { name: 'Gastos', href: '/gastos', icon: Receipt },
  { name: 'Caja Repuestos', href: '/caja-repuestos', icon: Cog },
  { name: 'Cierre de caja', href: '/cierre-caja', icon: Lock },
  { name: 'Comprobantes de caja', href: '/comprobantes-caja', icon: FileText },
  { name: 'Adelantos', href: '/adelantos', icon: DollarSign },
  { name: 'Planillas', href: '/pagos', icon: DollarSign },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Auditoría', href: '/auditoria', icon: ShieldAlert },
  { name: 'Importar Datos', href: '/importar-datos', icon: FileSpreadsheet },
  { name: 'Data Antigua', href: '/data-antigua', icon: Archive },
  { name: 'Ayuda / Manual', href: '/ayuda', icon: HelpCircle },
];

const ROL_TEXTO: Record<string, string> = { cajero: 'Cajero', dueno: 'Dueño', admin: 'Administrador' };

export default function Layout() {
  // 2026-10-22: nombre y rol del usuario conectado (sale del token de la sesión)
  const sesion = (() => { try { const t = localStorage.getItem('auth_token') || ''; const b = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'); const d = JSON.parse(decodeURIComponent(escape(atob(b)))); return { nombre: d.nombre || d.usuario, rol: d.rol }; } catch { return null; } })();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  function cerrarSesion() {
    localStorage.removeItem('auth_token');
    navigate('/login', { replace: true });
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Sidebar móvil */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-primary-900">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent navigation={navigation} currentPath={location.pathname} currentSearch={location.search} />
          </div>
        </div>
      )}

      {/* Sidebar desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent navigation={navigation} currentPath={location.pathname} currentSearch={location.search} />
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Header */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-primary-900 shadow">
          <button
            type="button"
            className="px-4 border-r border-primary-700 text-primary-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-400 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="relative w-full text-primary-300 focus-within:text-white">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                    {/* Search icon si lo necesitas */}
                  </div>
                </div>
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6 gap-4">
              <div className="text-sm text-primary-100">
                Sistema Mantsila v3.1
              </div>
              {sesion && (
                <div className="text-sm text-white font-semibold" title="Usuario con la sesión abierta">
                  {sesion.nombre} <span className="font-normal text-primary-200">({ROL_TEXTO[sesion.rol] || sesion.rol})</span>
                </div>
              )}
              <button
                type="button"
                onClick={cerrarSesion}
                className="flex items-center text-sm text-primary-200 hover:text-red-300"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Salir
              </button>
            </div>
          </div>
        </div>

        {/* Contenido de la página */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>

          {/* Footer con marca personal */}
          <footer className="bg-gray-50 border-t border-gray-200 mt-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3">
              <div className="flex items-center justify-center text-sm text-gray-600">
                <span className="font-medium text-red-500">
                  💻 Creado por JOHN ROMAN - Tu Lugar Inteligente
                </span>
                <span className="mx-2">•</span>
                <a
                  href="tel:931398916"
                  className="text-primary-700 hover:text-primary-800 font-medium transition-colors"
                >
                  📱 931398916
                </a>
              </div>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ navigation, currentPath, currentSearch }: {
  navigation: NavigationItem[];
  currentPath: string;
  currentSearch: string;
}) {
  return (
    <div className="flex-1 flex flex-col min-h-0 border-r border-primary-800 bg-primary-900">
      <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
        <div className="flex items-center flex-shrink-0 px-4">
          <div className="w-full">
            <h1 className="text-xl font-bold text-white">Sistema Mantsila</h1>
            <p className="text-xs text-primary-300 mt-1">v3.1</p>
          </div>
        </div>
        <nav className="mt-5 flex-1 px-2 space-y-1">
          {navigation.map((item) => {
            const [itemPath, itemQuery] = item.href.split('?');
            const isActive = itemQuery
              ? currentPath === itemPath && currentSearch === `?${itemQuery}`
              : currentPath.startsWith(itemPath) && !(itemPath === '/stock' && currentSearch.includes('tipo=herramienta'));
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive
                    ? 'bg-primary-700 text-white'
                    : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                }`}
              >
                <item.icon
                  className={`mr-3 h-5 w-5 ${
                    isActive ? 'text-white' : 'text-primary-300 group-hover:text-white'
                  }`}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Marca personal en sidebar */}
      <div className="flex-shrink-0 flex border-t border-primary-800 p-4">
        <div className="flex-shrink-0 w-full group block">
          <div className="text-center">
            <p className="text-xs text-primary-300 font-medium">
              💻 Creado por
            </p>
            <p className="text-xs font-semibold text-red-300">
              JOHN ROMAN
            </p>
            <p className="text-xs text-primary-300">
              Tu Lugar Inteligente
            </p>
            <a
              href="tel:931398916"
              className="text-xs text-primary-200 hover:text-white font-medium"
            >
              📱 931398916
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
